import React, { useEffect, useState } from 'react'
import ReactModal from 'react-modal'
import { useTranslation } from 'react-i18next'
import { getScheduledJobs, getUploadDataDetails, scheduleEmail } from '../../server/server'
import Loading from '../Loading'
import { ReactComponent as CloseModalIcon } from '../../assets/svg/CloseModalIcon.svg'
import { ReactComponent as GoNextIcon } from '../../assets/svg/GoNextIcon.svg'
import { ReactComponent as ScheduleIcon } from '../../assets/svg/ScheduleIcon.svg'
import { ReactComponent as Calendar } from '../../assets/svg/Calendar.svg'
import ScheduleEmailModal from './ScheduleEmailModal'
import ScheduledEmailsModal from './ScheduledEmailsModal'
import '../../styles/modals/PreviousUploadsModal.css'
import { fileSystemUrl } from '../../constants/Connections'
import { useUser } from '../../auth'
import { notify, reverseTimeDate } from '../../utils/util'

const INIT_EMAIL_SCHEDULING_DATA = {
    targetGroup: 'Teachers',
    userSelection: 'all', // 'all' or 'new'
    date: new Date(),
    label: '',
    saveToMasterlist: false,
    disablePasswordPrompt: false,
}

function PreviousUploadsModal({ modalIsOpen, afterOpenModal, closeModal }) {
    const { t } = useTranslation()
    const { user } = useUser()
    const [uploads, setUploads] = useState([])
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState(null)
    const [selectedUploadForDetails, setSelectedUploadForDetails] = useState(null)
    const [view, setView] = useState('list') // 'list', 'details', 'schedule-email', 'scheduled-emails'

    // Email scheduling states
    const [isScheduleEmailModalOpen, setIsScheduleEmailModalOpen] = useState(false)
    const [isScheduledEmailsModalOpen, setIsScheduledEmailsModalOpen] = useState(false)
    const [scheduledEmails, setScheduledEmails] = useState([])
    const [emailSchedulingData, setEmailSchedulingData] = useState(INIT_EMAIL_SCHEDULING_DATA)

    useEffect(() => {
        ReactModal.setAppElement('#root')
        if (modalIsOpen) {
            document.getElementById('home').style.filter = 'blur(5px)'
            fetchUploadsHistory()
        } else {
            document.getElementById('home').style.filter = 'unset'
            setEmailSchedulingData({ ...INIT_EMAIL_SCHEDULING_DATA, date: new Date() })
        }
    }, [modalIsOpen])

    const fetchUploadsHistory = async () => {
        setIsLoading(true)
        setError(null)
        try {
            const response = await getUploadDataDetails()
            if (response.status === 200) {
                setUploads(response.data || [])
            } else {
                setError('Failed to fetch uploads history')
            }
        } catch (err) {
            setError('Error fetching uploads history: ' + err.message)
        } finally {
            setIsLoading(false)
        }
    }

    const handleViewDetails = (upload) => {
        setSelectedUploadForDetails(upload)
        setView('details')
    }

    const handleBackToList = () => {
        setView('list')
        setSelectedUploadForDetails(null)
    }

    const handleScheduleEmail = async () => {
        const data = {
            type: 'send_email',
            date: emailSchedulingData.date.getTime(),
            clientTime: reverseTimeDate(emailSchedulingData.date).getTime(),
            institution_id: user.institution_id,
            targetGroup: emailSchedulingData.targetGroup,
            userSelection: emailSchedulingData.userSelection,
            task_id: selectedUploadForDetails.task_id,
            label: emailSchedulingData.label ?? `${emailSchedulingData.userSelection} ${emailSchedulingData.targetGroup} Email`,
            done: false,
            saveToMasterlist: emailSchedulingData.saveToMasterlist,
            disablePasswordPrompt: emailSchedulingData.disablePasswordPrompt,
        }

        const resp = await scheduleEmail(data)
        if (resp.status == 200) {
            setIsScheduleEmailModalOpen(false)
            notify(t('Email scheduled successfully'), 'success')
        }
    }

    const fetchScheduledEmails = async () => {
        const resp = await getScheduledJobs(selectedUploadForDetails.task_id)
        if (resp.status == 200) {
            console.log(resp.data)

            setScheduledEmails(resp.data || [])
        }
    }

    const handleEmailDeleted = (deletedTaskId) => {
        // Remove the deleted email from the local state
        setScheduledEmails((prev) => prev.filter((email) => email.id !== deletedTaskId))
    }

    const updateEmailSchedulingData = (field, value) => {
        setEmailSchedulingData((prev) => ({
            ...prev,
            [field]: value,
        }))
    }

    const renderUploadDetails = (upload) => {
        const results = upload.results || {}
        const hasResults = Object.keys(results).length > 0
        const file = upload.file || {}

        return (
            <div className="space-y-6">
                {/* Basic Information */}
                <div className="bg-lightBG rounded-lg p-6">
                    <h3 className="text-[18px] font-bold text-deepPurple mb-4">{t('Basic Information')}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <span className="text-mudPurple text-[14px] font-medium">{t('Status')}:</span>
                            <p className="font-semibold text-deepPurple text-[16px]">
                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(upload.status)}`}>{upload.status || 'Unknown'}</span>
                            </p>
                        </div>
                        <div>
                            <span className="text-mudPurple text-[14px] font-medium">{t('Progress')}:</span>
                            <p className="font-semibold text-deepPurple text-[16px]">{upload.progress || 0}%</p>
                        </div>
                        <div>
                            <span className="text-mudPurple text-[14px] font-medium">{t('Created At')}:</span>
                            <p className="font-semibold text-deepPurple text-[16px]">{formatDate(upload.created_at?.$date || upload.created_at)}</p>
                        </div>
                        {upload.updated_at && (
                            <div>
                                <span className="text-mudPurple text-[14px] font-medium">{t('Updated At')}:</span>
                                <p className="font-semibold text-deepPurple text-[16px]">{formatDate(upload.updated_at?.$date || upload.updated_at)}</p>
                            </div>
                        )}
                    </div>
                    {upload.message && (
                        <div className="mt-4">
                            <span className="text-mudPurple text-[14px] font-medium">{t('Message')}:</span>
                            <p className="font-semibold text-deepPurple text-[16px] mt-1">{upload.message}</p>
                        </div>
                    )}
                </div>

                {/* File Information */}
                {file && Object.keys(file).length > 0 && (
                    <div className="bg-lightBG rounded-lg p-6">
                        <h3 className="text-[18px] font-bold text-deepPurple mb-4">{t('File Information')}</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {!!file?.old_name && !!user?.token && (
                                <div>
                                    <span className="text-mudPurple text-[14px] font-medium">{t('File Name')}:</span>
                                    <div>
                                        <a
                                            href={`${fileSystemUrl}/${file.storage_name}?token=${user.token}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="font-semibold text-deepPurple text-[16px]"
                                        >
                                            {file.old_name}
                                        </a>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Processing Results */}
                {hasResults && (
                    <div className="bg-lightBG rounded-lg p-6">
                        <h3 className="text-[18px] font-bold text-deepPurple mb-4">{t('Processing Results')}</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {/* Students Section */}
                            <div className="bg-whiteContrast rounded-lg p-4">
                                <h4 className="text-[16px] font-semibold text-deepPurple mb-3 flex items-center">
                                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
                                        />
                                    </svg>
                                    {t('Students')}
                                </h4>
                                <div className="space-y-2 text-sm">
                                    {results.primary_students_processed !== undefined && (
                                        <div className="flex justify-between">
                                            <span className="text-mudPurple">{t('Primary')}:</span>
                                            <span className="font-medium text-deepPurple">{results.primary_students_processed}</span>
                                        </div>
                                    )}
                                    {results.secondary_students_processed !== undefined && (
                                        <div className="flex justify-between">
                                            <span className="text-mudPurple">{t('Secondary')}:</span>
                                            <span className="font-medium text-deepPurple">{results.secondary_students_processed}</span>
                                        </div>
                                    )}
                                    {results.students_added !== undefined && (
                                        <div className="flex justify-between">
                                            <span className="text-mudPurple">{t('Added')}:</span>
                                            <span className="font-medium text-green-600">{results.students_added}</span>
                                        </div>
                                    )}
                                    {results.students_updated !== undefined && (
                                        <div className="flex justify-between">
                                            <span className="text-mudPurple">{t('Updated')}:</span>
                                            <span className="font-medium text-blue-600">{results.students_updated}</span>
                                        </div>
                                    )}
                                    {results.animals_assigned !== undefined && (
                                        <div className="flex justify-between">
                                            <span className="text-mudPurple">{t('Animals Assigned')}:</span>
                                            <span className="font-medium text-deepPurple">{results.animals_assigned}</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Staff Section */}
                            <div className="bg-whiteContrast rounded-lg p-4">
                                <h4 className="text-[16px] font-semibold text-deepPurple mb-3 flex items-center">
                                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6.759"
                                        />
                                    </svg>
                                    {t('Staff')}
                                </h4>
                                <div className="space-y-2 text-sm">
                                    {results.staff_processed !== undefined && (
                                        <div className="flex justify-between">
                                            <span className="text-mudPurple">{t('Processed')}:</span>
                                            <span className="font-medium text-deepPurple">{results.staff_processed}</span>
                                        </div>
                                    )}
                                    {results.staff_added !== undefined && (
                                        <div className="flex justify-between">
                                            <span className="text-mudPurple">{t('Added')}:</span>
                                            <span className="font-medium text-green-600">{results.staff_added}</span>
                                        </div>
                                    )}
                                    {results.staff_updated !== undefined && (
                                        <div className="flex justify-between">
                                            <span className="text-mudPurple">{t('Updated')}:</span>
                                            <span className="font-medium text-blue-600">{results.staff_updated}</span>
                                        </div>
                                    )}
                                    {results.admin_created !== undefined && (
                                        <div className="flex justify-between">
                                            <span className="text-mudPurple">{t('Admin Created')}:</span>
                                            <span className="font-medium text-green-600">{results.admin_created}</span>
                                        </div>
                                    )}
                                    {results.hoy_created !== undefined && (
                                        <div className="flex justify-between">
                                            <span className="text-mudPurple">{t('HOY Created')}:</span>
                                            <span className="font-medium text-green-600">{results.hoy_created}</span>
                                        </div>
                                    )}
                                    {results.teachers_assigned !== undefined && (
                                        <div className="flex justify-between">
                                            <span className="text-mudPurple">{t('Teachers Assigned')}:</span>
                                            <span className="font-medium text-deepPurple">{results.teachers_assigned}</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Classes Section */}
                            <div className="bg-whiteContrast rounded-lg p-4">
                                <h4 className="text-[16px] font-semibold text-deepPurple mb-3 flex items-center">
                                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                                        />
                                    </svg>
                                    {t('Classes')}
                                </h4>
                                <div className="space-y-2 text-sm">
                                    {results.classes_created !== undefined && (
                                        <div className="flex justify-between">
                                            <span className="text-mudPurple">{t('Created')}:</span>
                                            <span className="font-medium text-green-600">{results.classes_created}</span>
                                        </div>
                                    )}
                                    {results.classes_populated !== undefined && (
                                        <div className="flex justify-between">
                                            <span className="text-mudPurple">{t('Populated')}:</span>
                                            <span className="font-medium text-blue-600">{results.classes_populated}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Master List Information */}
                        {(results.master_list_updated !== undefined || (results.master_list_stats && Object.keys(results.master_list_stats).length > 0)) && (
                            <div className="mt-6 bg-whiteContrast rounded-lg p-4">
                                <h4 className="text-[16px] font-semibold text-deepPurple mb-3 flex items-center">
                                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                        />
                                    </svg>
                                    {t('Master List')}
                                </h4>
                                <div className="space-y-2 text-sm">
                                    {results.master_list_updated !== undefined && (
                                        <div className="flex justify-between">
                                            <span className="text-mudPurple">{t('Updated')}:</span>
                                            <span className={`font-medium ${results.master_list_updated ? 'text-green-600' : 'text-gray-600'}`}>
                                                {results.master_list_updated ? t('Yes') : t('No')}
                                            </span>
                                        </div>
                                    )}
                                    {results.master_list_stats && Object.keys(results.master_list_stats).length > 0 && (
                                        <div className="mt-2">
                                            <span className="text-mudPurple text-xs">{t('Master List Stats')}:</span>
                                            <a
                                                href={`${fileSystemUrl}/upload/${results.master_list_stats.filename}?token=${user.token}`}
                                                className="text-xs text-deepPurple mt-1 bg-lightBG p-2 rounded"
                                            >
                                                Download master-list
                                            </a>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Errors and Warnings */}
                {(upload.errors?.length > 0 || upload.warnings?.length > 0) && (
                    <div className="bg-lightBG rounded-lg p-6">
                        <h3 className="text-[18px] font-bold text-deepPurple mb-4">{t('Issues')}</h3>

                        {upload.errors?.length > 0 && (
                            <div className="mb-4">
                                <h4 className="text-[16px] font-semibold text-deleteRed mb-2 flex items-center">
                                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z"
                                        />
                                    </svg>
                                    {t('Errors')} ({upload.errors.length})
                                </h4>
                                <div className="space-y-2">
                                    {upload.errors.map((error, index) => (
                                        <div key={index} className="bg-red-50 border border-red-200 rounded p-3">
                                            <p className="text-red-800 text-sm">{error}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {upload.warnings?.length > 0 && (
                            <div>
                                <h4 className="text-[16px] font-semibold text-yellow-600 mb-2 flex items-center">
                                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    {t('Warnings')} ({upload.warnings.length})
                                </h4>
                                <div className="space-y-2">
                                    {upload.warnings.map((warning, index) => (
                                        <div key={index} className="bg-yellow-50 border border-yellow-200 rounded p-3">
                                            <p className="text-yellow-800 text-sm">{warning}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Action Buttons */}
                <div className="bg-lightBG rounded-lg p-6 mt-6">
                    <h3 className="text-[18px] font-bold text-deepPurple mb-4">{t('Actions')}</h3>
                    <div className="flex flex-wrap gap-4">
                        <button
                            onClick={() => setIsScheduleEmailModalOpen(true)}
                            className="flex items-center gap-3 bg-deepPurple hover:bg-darkPurple text-white px-6 py-4 rounded-xl font-semibold transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105"
                        >
                            <div className="bg-white bg-opacity-20 p-2 rounded-lg">
                                <ScheduleIcon className="w-5 h-5" />
                            </div>
                            <div className="text-left">
                                <div className="text-[16px] font-semibold">{t('Schedule email for new users')}</div>
                                <div className="text-[12px] opacity-80">{t('Send emails to users based on timezone')}</div>
                            </div>
                        </button>

                        <button
                            onClick={() => setIsScheduledEmailsModalOpen(true)}
                            className="flex items-center gap-3 bg-mudPurple hover:bg-lightPurpleGray text-white px-6 py-4 rounded-xl font-semibold transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105"
                        >
                            <div className="bg-white bg-opacity-20 p-2 rounded-lg">
                                <Calendar className="w-5 h-5" color="#AFAEC6" />
                            </div>
                            <div className="text-left">
                                <div className="text-[16px] font-semibold">{t('View scheduled emails')}</div>
                                <div className="text-[12px] opacity-80">{t('Manage your email schedule')}</div>
                            </div>
                        </button>
                    </div>
                </div>
            </div>
        )
    }

    const formatDate = (dateString) => {
        try {
            // Handle MongoDB date format
            if (typeof dateString === 'object' && dateString.$date) {
                return new Date(dateString.$date).toLocaleString()
            }
            return new Date(dateString).toLocaleString()
        } catch {
            return dateString || 'N/A'
        }
    }

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes'
        if (!bytes || bytes < 0) return 'N/A'

        const k = 1024
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
        const i = Math.floor(Math.log(bytes) / Math.log(k))

        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    }

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'completed':
            case 'success':
                return 'text-deepPurple bg-highlightColor'
            case 'failed':
            case 'error':
                return 'text-white bg-deleteRed'
            case 'pending':
            case 'processing':
                return 'text-deepPurple bg-containerGray'
            default:
                return 'text-mudPurple bg-lightBG'
        }
    }

    return (
        <>
            <ScheduleEmailModal
                isOpen={isScheduleEmailModalOpen}
                onClose={() => setIsScheduleEmailModalOpen(false)}
                emailSchedulingData={emailSchedulingData}
                updateEmailSchedulingData={updateEmailSchedulingData}
                onSchedule={handleScheduleEmail}
            />
            <ScheduledEmailsModal
                isOpen={isScheduledEmailsModalOpen}
                onClose={() => setIsScheduledEmailsModalOpen(false)}
                scheduledEmails={scheduledEmails}
                onAfterOpen={fetchScheduledEmails}
                onEmailDeleted={handleEmailDeleted}
            />

            <ReactModal
                transparent
                isOpen={modalIsOpen}
                onAfterOpen={afterOpenModal}
                onRequestClose={closeModal}
                contentLabel="Previous Uploads Modal"
                ariaHideApp={false}
                onAfterClose={() => {
                    setUploads([])
                    setError(null)
                    setIsLoading(false)
                    setSelectedUploadForDetails(null)
                    setView('list')
                }}
                style={{
                    overlay: {
                        zIndex: 99,
                    },
                    content: {
                        inset: 0,
                        padding: 0,
                        margin: 'auto',
                        marginTop: 30,
                        opacity: 1,
                        width: 800,
                        backgroundColor: 'transparent',
                        maxHeight: '92vh',
                        border: 'none',
                        borderRadius: 10,
                        boxShadow: 'none',
                        overflow: 'hidden',
                        position: 'relative',
                    },
                }}
            >
                <div
                    style={{
                        position: 'absolute',
                        top: 0,
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    <div className="day-reflection-modal-icon" onClick={closeModal}>
                        <CloseModalIcon />
                    </div>
                </div>

                <div className="mt-[30px] rounded-[10px] bg-whiteContrast">
                    {/* Header */}
                    <div className="pt-[49px] pb-[22px] rounded-[10px] bg-lightBG relative">
                        {view === 'details' && (
                            <button
                                className="flex flex-row items-center justify-center gap-[10px] bg-white rounded-full px-[15px] h-[40px] absolute left-6 top-1/2 transform -translate-y-1/2"
                                onClick={handleBackToList}
                            >
                                <GoNextIcon />
                                <p className="text-[16px] leading-5 text-deepPurple font-semibold">{t('back')}</p>
                            </button>
                        )}
                        <p className="text-[20px] font-bold leading-[25px] text-deepPurple text-center">{view === 'details' ? t('Upload Details') : t('Previous Uploads')}</p>
                    </div>

                    {/* Content */}
                    <div className="max-h-[calc(92vh-126px)] overflow-auto grey-small-scroll p-6">
                        <div className="p-10 w-full flex flex-col">
                            {view === 'details' && selectedUploadForDetails ? (
                                renderUploadDetails(selectedUploadForDetails)
                            ) : isLoading ? (
                                <div className="flex justify-center items-center h-[200px]">
                                    <Loading />
                                </div>
                            ) : error ? (
                                <div className="flex flex-col justify-center items-center h-[200px]">
                                    <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
                                        <div className="flex items-center space-x-3">
                                            <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z"
                                                />
                                            </svg>
                                            <div>
                                                <h3 className="text-red-800 font-medium">{t('Error')}</h3>
                                                <p className="text-red-600 text-sm mt-1">{error}</p>
                                            </div>
                                        </div>
                                        <button onClick={fetchUploadsHistory} className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">
                                            {t('Retry')}
                                        </button>
                                    </div>
                                </div>
                            ) : uploads.length === 0 ? (
                                <div className="flex flex-col justify-center items-center h-[200px]">
                                    <div className="text-center">
                                        <svg className="w-16 h-16 text-mudPurple mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={1.5}
                                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                            />
                                        </svg>
                                        <h3 className="text-mudPurple text-lg font-medium">{t('No Previous Uploads')}</h3>
                                        <p className="text-mudPurple mt-2">{t('No upload history found')}</p>
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {uploads.map((upload, index) => (
                                        <div
                                            key={upload._id?.$oid || upload.id || index}
                                            className="bg-lightBG border border-deselectPurple rounded-lg p-6 hover:bg-containerGray transition-colors cursor-pointer"
                                            onClick={() => handleViewDetails(upload)}
                                        >
                                            <div className="flex justify-between items-start">
                                                <div className="flex-1">
                                                    <div className="flex items-center space-x-3 mb-3">
                                                        <h3 className="text-lg font-semibold text-deepPurple">
                                                            {upload.file?.old_name || upload.task_id || upload.filename || upload.file_name || `Upload ${index + 1}`}
                                                        </h3>
                                                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(upload.status)}`}>
                                                            {upload.status || 'Unknown'}
                                                        </span>
                                                    </div>

                                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                                        <div>
                                                            <span className="text-mudPurple text-[14px] font-medium">{t('Created At')}:</span>
                                                            <p className="font-semibold text-deepPurple text-[16px]">{formatDate(upload.created_at)}</p>
                                                        </div>

                                                        {upload.results && (
                                                            <>
                                                                {(upload.results.primary_students_processed !== undefined ||
                                                                    upload.results.secondary_students_processed !== undefined) && (
                                                                    <div>
                                                                        <span className="text-mudPurple text-[14px] font-medium">{t('Students')}:</span>
                                                                        <p className="font-semibold text-deepPurple text-[16px]">
                                                                            {(upload.results.primary_students_processed || 0) + (upload.results.secondary_students_processed || 0)}
                                                                        </p>
                                                                    </div>
                                                                )}

                                                                {upload.results.staff_processed !== undefined && (
                                                                    <div>
                                                                        <span className="text-mudPurple text-[14px] font-medium">{t('Staff')}:</span>
                                                                        <p className="font-semibold text-deepPurple text-[16px]">{upload.results.staff_processed}</p>
                                                                    </div>
                                                                )}

                                                                {upload.results.classes_created !== undefined && (
                                                                    <div>
                                                                        <span className="text-mudPurple text-[14px] font-medium">{t('Classes')}:</span>
                                                                        <p className="font-semibold text-deepPurple text-[16px]">{upload.results.classes_created}</p>
                                                                    </div>
                                                                )}
                                                            </>
                                                        )}
                                                    </div>

                                                    {upload.message && <p className="text-mudPurple mt-3 text-[14px]">{upload.message}</p>}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </ReactModal>
        </>
    )
}

export default PreviousUploadsModal
